import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { FrequencyPattern, CreateChallengeInput } from '@/types/challenge'
import { useUserChallenges } from '../UserChallengeProvider'
import { useTranslation } from '@/i18n/hooks/useTranslation'
import { FrequencyConfigurationStep } from '../components/FrequencyConfigurationStep'
import { scheduleNotificationsForChallenge } from '@/utils/simpleChallengeUtils'


export function ChallengeFrequencyPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const { challengeRepository } = useUserChallenges()

  // Get data from navigation state or redirect to selection
  const { selectedChallenge, customTitle: initialCustomTitle } = location.state || {}
  
  // Redirect to selection if no data
  useEffect(() => {
    if (!location.state) {
      navigate('/challenges/new/selection')
    }
  }, [location.state, navigate])

  const [customTitle, setCustomTitle] = useState(initialCustomTitle || '')
  const [frequency, setFrequency] = useState<FrequencyPattern>({
    mode: 'daily',
    times: ['09:00'],
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  })


  // Set default frequency based on challenge
  useEffect(() => {
    if (selectedChallenge) {
      const defaultFreq = { ...selectedChallenge.defaultFrequency }
      if (defaultFreq.mode === 'daily' && (!defaultFreq.times || defaultFreq.times.length === 0)) {
        defaultFreq.times = ['09:00']
      }
      setFrequency(defaultFreq)
    }
  }, [selectedChallenge])

  const createChallenge = useMutation({
    mutationFn: async (input: CreateChallengeInput) => {
      return challengeRepository.create(input)
    },
    onSuccess: async (challenge) => {
      // Invalidate all relevant caches
      queryClient.invalidateQueries({ queryKey: ['challenges'] })
      queryClient.invalidateQueries({ queryKey: ['joined-challenges'] })
      queryClient.invalidateQueries({ queryKey: ['user-challenges'] })
      
      // Navigate to success step
      navigate('/challenges/new/success', {
        state: {
          selectedChallenge,
          customTitle,
          createdChallenge: challenge,
          step: 'success'
        }
      })
      
      // Schedule notifications for the new challenge
      try {
        await scheduleNotificationsForChallenge(challenge)
        if (import.meta.env.DEV) {
          console.log('Notifications scheduled for new challenge:', challenge.title)
        }
      } catch (error) {
        console.error('Failed to schedule notifications for challenge:', error)
        // Don't show error to user as challenge was created successfully
      }
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to create challenge')
    },
  })

  const handleFrequencyNext = () => {
    // Validate and create the challenge
    const title = selectedChallenge ? selectedChallenge.name : customTitle.trim()
    const type = selectedChallenge ? 'catalog' : 'custom'

    if (!title) {
      toast.error('Please enter a challenge title')
      return
    }

    if (title.length < 3 || title.length > 60) {
      toast.error('Challenge title must be between 3 and 60 characters')
      return
    }

    // Validate frequency
    if (frequency.mode === 'daily' && (!frequency.times || frequency.times.length === 0)) {
      toast.error('Please select at least one time')
      return
    }

    if (frequency.mode === 'weekly' && (!frequency.daysOfWeek || frequency.daysOfWeek.length === 0)) {
      toast.error('Please select at least one day')
      return
    }

    const input: CreateChallengeInput = {
      title,
      type,
      frequency,
      challengeId: selectedChallenge?.id,
    }

    createChallenge.mutate(input)
  }

  const handleFrequencyBack = () => {
    navigate('/challenges/new/selection')
  }

  // Show loading if no state data
  if (!location.state) {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-md"></div>
          <p className="text-text">{t('common.loading')}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-bg">
      <div className="max-w-2xl mx-auto px-lg py-2xl">
        <div className="rounded-xl">
          {/* Progress Indicator */}
          <div className="mb-2xl">
            <div className="flex items-center justify-center space-x-md">
              <button
                onClick={() => navigate('/challenges/new/selection')}
                className="flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium bg-primary text-white dark:text-black cursor-pointer hover:scale-110 hover:shadow-lg transition-all duration-200"
              >
                1
              </button>
              <div className="w-16 h-1 bg-primary" />
              <div className="flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium bg-primary text-white dark:text-black">
                2
              </div>
              <div className="w-16 h-1 bg-border" />
              <div className="flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium bg-border text-text-muted">
                3
              </div>
            </div>
          </div>

          {/* Step Content */}
          <FrequencyConfigurationStep
            selectedChallenge={selectedChallenge}
            customTitle={customTitle}
            frequency={frequency}
            onTitleChange={setCustomTitle}
            onFrequencyChange={setFrequency}
            onNext={handleFrequencyNext}
            onBack={handleFrequencyBack}
          />

          {/* Loading State */}
          {createChallenge.isPending && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <div className="rounded-xl p-xl text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-md"></div>
                <p className="text-text">{t('challenges.creatingChallenge')}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
} 